import { useEffectEvent, useLayoutEffect } from "react";
import { usePreviewContext } from "../contexts/preview-context";
import { useEditor } from "./useEditor";

/** Hook that exposes functions that can be used to simplify automation (e2e) */
export function useAutomation() {
	// TODO: Should we add process.env condition to make it conditional?

	const { slideShowPreviewRef, getSpreadToScreenTransform } = usePreviewContext();
	const editor = useEditor();

	/** Function to get a zone */
	function getSpread() {
		// Get spread selected in view
		const slideShow = slideShowPreviewRef?.current?.slideShow;
		return slideShow?.items[slideShow?.index]?.spread;
	}

	/** Function that returns page information that can be used to automate tasks */
	const getPageInfo = useEffectEvent(function getPageInfo({
		index = 0,
	}: {
		index?: number;
	} = {}) {
		const spread = getSpread();
		if (!spread) return undefined;

		const page = spread.pageZones[index];
		if (!page) return undefined;

		// Quick and dirty way to find a location to click next to the zones
		// calculate the outerbox of all zones
		let left = Infinity;
		let top = Infinity;
		let right = -Infinity;
		let bottom = -Infinity;

		page.getAllZones().forEach((zone) => {
			const rect = zone.spreadInnerBounds;
			if (rect.left < left) left = rect.left;
			if (rect.right > right) right = rect.right;
			if (rect.top < top) top = rect.top;
			if (rect.bottom > bottom) bottom = rect.bottom;
		});

		const zoneBoundingBox = eXpress.math.rect(left, top, right - left, bottom - top);

		// Find a click location
		const selectPoint = eXpress.math.rect(page).center();
		if (zoneBoundingBox.left > page.left) selectPoint.left = zoneBoundingBox.left / 2;
		else if (zoneBoundingBox.top > page.top) selectPoint.top = zoneBoundingBox.top / 2;
		else if (zoneBoundingBox.right < page.left + page.width) {
			selectPoint.left =
				zoneBoundingBox.right + (page.left + page.width - zoneBoundingBox.right) / 2;
		} else if (zoneBoundingBox.bottom < page.top + page.height) {
			selectPoint.top =
				zoneBoundingBox.bottom + (page.top + page.height - zoneBoundingBox.bottom) / 2;
		} else {
			// eslint-disable-next-line no-console
			console.error(
				`Page: ${eXpress.math
					.rect(page)
					.toString()}, zone bounding box: ${zoneBoundingBox.toString()}`,
			);
			throw new Error(`No click position found for the page index ${index}`);
		}

		const spreadToScreenTransform = getSpreadToScreenTransform(spread, undefined);
		if (!spreadToScreenTransform)
			throw new Error(`Could not get transform for ${spread.toString()}.`);

		return {
			selectPoint: spreadToScreenTransform.transformPoint(selectPoint),
		};
	});

	/** Function to get a zone */
	function getZone(
		spread: eXpress.core.Spread,
		{
			index = 0,
			filter,
		}: {
			index?: number;
			filter?: "PhotoZone" | "TextZone" | undefined;
		} = {},
	) {
		// Get zone matching the filter
		return spread?.zones.filter((z) => {
			switch (filter) {
				case "PhotoZone":
					return eXpress.core.isPhotoZone(z);
				case "TextZone":
					return eXpress.core.isAnyTextZone(z);
				case undefined:
					return true;
				default: {
					// eslint-disable-next-line no-console, @typescript-eslint/restrict-template-expressions
					console.warn(`_getZoneCoordinate: Unknown filter: ${filter}`);
					return true;
				}
			}
		})[index];
	}

	/** Function that returns zone information that can be used to automate tasks */
	const getZoneInfo = useEffectEvent(function getZoneInfo({
		index = 0,
		filter,
	}: {
		index?: number;
		filter?: "PhotoZone" | "TextZone" | undefined;
	} = {}) {
		const spread = getSpread();
		if (!spread) return undefined;

		const zone = index >= 0 ? getZone(spread, { index, filter }) : undefined;
		if (!zone) return undefined;

		const spreadToScreenTransform = getSpreadToScreenTransform(spread, undefined);
		if (!spreadToScreenTransform)
			throw new Error(`Could not get transform for ${spread.toString()}.`);

		/** Box containing (rotated) zone with border effects */
		const outerBox = zone
			? spreadToScreenTransform.transformRect(eXpress.math.rect(zone.spreadOuterBounds))
			: undefined;
		const selectPoint = zone
			? spreadToScreenTransform.transformPoint(
					eXpress.math.rect(zone.spreadInnerBounds).center(),
			  )
			: undefined;

		return {
			outerBox,
			/** Not checked for overlapping zones */
			selectPoint,
		};
	});

	const waitUntilZoneSelected = useEffectEvent(function waitUntilZoneSelected({
		index = 0,
		filter,
	}: {
		index?: number;
		filter?: "PhotoZone" | "TextZone" | undefined;
	} = {}) {
		return new Promise<boolean>((resolve) => {
			const spread = getSpread();
			if (!spread) return;
			const zone = index >= 0 ? getZone(spread, { index, filter }) : undefined;
			if (!zone) return;
			const isSelected = (z: eXpress.core.IZone | undefined | null) => z && z === zone;

			if (isSelected(editor.selection.getCurrentZone())) {
				resolve(true);
				return;
			}

			editor.broadcaster.subscribeUntil((message) => {
				if (eXpress.core.isSelectedZoneChanged(message)) {
					// Optional
					if (isSelected(editor.selection.getCurrentZone())) {
						resolve(true);
						return true;
					}
				}
				return false;
			});
		});
	});

	// Set functions once
	useLayoutEffect(() => {
		(window as any)["__automation"] = {
			getZoneInfo,
			getPageInfo,
			waitUntilZoneSelected,
		};
	}, []);

	/** Function that returns current zoom/scale information */
	function getZoomInfo() {
		const slideShow = slideShowPreviewRef?.current?.slideShow;
		if (!slideShow) return undefined;

		const currentItem = slideShow.items[slideShow.index];
		if (!currentItem) return undefined;

		const currentScale = slideShow.calcViewBoxScale(currentItem, {
			newViewBox: slideShow.viewBox,
		});

		return {
			scale: currentScale,
			viewBox: slideShow.viewBox,
		};
	}

	// Allow, this is only used for debugging
	// eslint-disable-next-line react-compiler/react-compiler
	(window as any)["__automation"] = {
		getZoneInfo,
		getPageInfo,
		getZoomInfo,
		waitUntilZoneSelected,
	};
}
